import { useEffect, useMemo, useRef, useState } from 'react';
import { todayIsoInZone } from '../../../hooks/useStoreTimezone';
// [3.5 #4] 预览里的商品链接要可点(htmlToText 会把地址丢掉,见 productLinks.ts 顶注)。
import { bodySegments } from '../../../lib/utils/productLinks';
import { DraftBodyText } from '../../../components/agent-chat/DraftBodyText';
import RichDraftEditor from '../../../components/agent-chat/RichDraftEditor';
import { variantCode } from '../../../lib/utils/variantCode';
import { HERO_IMG_FIT } from './draftContent';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
// [高保真 · Figma 24244-10568] Edit / Regenerate 用稿里的品牌紫描边图标
import PencilSvg from '../../../assets/maya-icons/pencil-01.svg';
import RefreshCwSvg from '../../../assets/maya-icons/refresh-cw-01.svg';
import {
  CALENDAR_PLATFORMS,
  platformMeta,
  setupLabel,
  type CalendarItem,
  type PlatformKey,
  type PlatformVersion,
} from './calendarContent';

/**
 * [需求⑯ 定时任务·单条详情弹窗] 点日历某天的 chip / Content Pool 某条 → 打开此弹窗。
 *
 * 一条排期任务的操作面板：改期（日期/时间下拉）、逐平台 tab 切换（Shopify Blog / Instagram / X）、
 * 失败版本的 Retry、正文预览 + 内联 Edit（编辑态：标题输入 + textarea + 字数 x/limit + Cancel/Save）、
 * 只重生成当前平台版本（可选 note）、底部 Discard Task / Publish Now / Done。
 *
 * 纯前端 mock：Retry / Regenerate / Publish Now / 改期落库 / Discard 全部预留回调，接法见 calendarContent.ts 顶注。
 * [PRD 2026-08-18] Regenerate 不再弹窗内闭环：整篇改稿统一跳 Maya Chat（onRegenerateInChat），走 ⑥ 澄清流程。
 */

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** 'YYYY-MM-DD' → 'Aug 4'（下拉当前值展示）。 */
function dayLabel(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTH_ABBR[(m ?? 1) - 1]} ${d}`;
}

/** 该月的天数（用于日期下拉选项）。 */
function daysInMonth(iso: string): number {
  const [y, m] = iso.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

/** 这条发布失败是不是「平台嫌内容本身」——重发治不好的那种。
 *
 *  为什么读文案而不是读错误码:失败原因从发布器经 chatbot-api 的
 *  `/item/publish-failed` 落库时只剩一个 reason 字符串,码在那一跳丢了。这里认的
 *  是我们自己写的那句(「capped at 2200 characters and this one is 2418」)和平台
 *  退回的同类说法。认不出来就当成一时的失败 —— 那条路(Publish again)本来就在,
 *  少提示一次好过错拦一次重发。
 */
export function rejectedForItsContent(reason?: string | null): boolean {
  const t = (reason || '').toLowerCase();
  if (!t) return false;
  const tooLong =
    /\b(caption|copy|text|message|post)\b[^.]{0,60}\b(too long|over the limit|exceeds)\b/.test(t) ||
    /\b(capped|cap|limit(ed)?)\b[^.]{0,40}\bcharacters?\b/.test(t) ||
    /\bcharacters?\b[^.]{0,40}\b(limit|maximum|max|cap)\b/.test(t);
  return tooLong;
}

const TIME_OPTIONS = [
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
];

/** Figma 里那种「文字 + ▾、无原生箭头」的下拉：透明 native select 盖在样式层上。 */
function PillSelect({
  value,
  label,
  options,
  onChange,
}: {
  value: string;
  label: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative inline-flex">
      <div className="pointer-events-none inline-flex min-w-[7rem] items-center justify-between gap-3 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900">
        <span>{label}</span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
        aria-label={label}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function ScheduledItemModal({
  item,
  onClose,
  onReschedule,
  onRetry,
  onPublishAsIs,
  onRegenerateInChat,
  onSaveVersion,
  onPublishNow,
  onDiscard,
  readOnly = false,
}: {
  item: CalendarItem;
  onClose: () => void;
  onReschedule?: (date: string, time: string) => void;
  /** [2026-08-28] 第二个参数是商家在下面那个框里写的要求(如有)——重做要带着它,
   *  否则商家刚说完"要更活泼"却只换来一次原样重跑。
   *  返回 promise 的话,按钮在它 settle 前是 pending 态(这一下要开票 + 查会话)。 */
  onRetry?: (platform: PlatformKey, note?: string) => void | Promise<void>;
  /** 「原样再发一次」—— 只对发布失败出现,且是次要动作:内容没问题、失败是一时的
   *  (令牌过期、平台抖动)时才用它。默认动作是重做,因为发布失败的原因常常就是
   *  内容本身。 */
  onPublishAsIs?: (platform: PlatformKey) => void;
  /** [需求⑥/⑯·PRD 2026-08-18] 改稿入口统一到 chat：整篇跳 Maya Chat，不在弹窗内闭环。
   *  空批注 → 发「Regenerate this post」；有批注 → 发那句(视为整篇、不追问平台)。 */
  /** 带上当前这一格的渠道 —— 改稿的身份靠票据担保,而票是按 (行, 渠道) 开的。 */
  onRegenerateInChat?: (note: string, platform: PlatformKey) => void | Promise<void>;
  onSaveVersion?: (platform: PlatformKey, title: string, body: string) => void;
  onPublishNow?: () => void;
  onDiscard?: () => void;
  /** 已发布的条目:可查看,不可编辑与撤回(PRD #⑯)。改期下拉、Edit this version、
   *  Discard Task 一律不渲染 —— 已经发出去的帖子改这边的副本毫无意义,而 Discard
   *  会把发布记录对应的存储行真删掉。有原帖地址时给「View post」跳平台。 */
  readOnly?: boolean;
}) {
  const [activePlatform, setActivePlatform] = useState<PlatformKey>(
    // 默认停在失败的那个平台（Figma：Shopify Blog 失败即默认高亮它），否则第一个。
    item.versions.find(v => v.status === 'failed')?.platform ??
      item.versions[0]?.platform ??
      'shopify_blog'
  );
  const [date, setDate] = useState<string>(item.date ?? '');
  const [time, setTime] = useState<string>(item.time);
  const [note, setNote] = useState('');

  // 编辑态
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');

  /** 重做/改稿的 in-flight 态。这两个动作在跳转前要先开票 + 反查历史会话,dev 实测
   *  能到几秒;没有 pending 态时按钮毫无变化,商家看到的就是"点了没反应"然后再点一次。
   *  同时也当双击闸门 —— 重复点会开出多张重做票。
   *  跳转成功时本组件已卸载,所以 settle 后要看 mounted 再置回,别对着卸载的组件 setState。 */
  const [busyAction, setBusyAction] = useState<null | 'retry' | 'regen'>(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const runAction = async (kind: 'retry' | 'regen', fn: () => void | Promise<void>) => {
    if (busyAction) return;
    setBusyAction(kind);
    try {
      await fn();
    } finally {
      if (mountedRef.current) setBusyAction(null);
    }
  };

  const version: PlatformVersion | undefined = useMemo(
    () => item.versions.find(v => v.platform === activePlatform),
    [item.versions, activePlatform]
  );

  // [3.5 #8 · 验收 #12] 只显示这条内容**真的建过**的渠道 tab。原来 tab 栏铺的是静态
  // CALENDAR_PLATFORMS(Blog / IG / FB 三个全量),于是只生成了 IG 的一篇,弹窗里照样
  // 顶着 Blog 和 Facebook 两个空 tab —— 切过去是一片空,商家以为另外两篇也生成了。
  // 渠道口径一律以「实际建了哪几版」为准(activePlatform 本就从 versions 取默认值,
  // 只有这条 tab 栏是漏网的)。按 CALENDAR_PLATFORMS 的固定顺序过滤,保留 Blog→IG→FB
  // 的排列;万一某条 version 的 platform 不在表里也不至于把它藏掉。空版本(理论上不该
  // 发生)时回落到全量,免得弹窗一个 tab 都没有。
  const platformTabs = useMemo(() => {
    const present = new Set(item.versions.map(v => v.platform));
    const known = CALENDAR_PLATFORMS.filter(p => present.has(p.key));
    const extras = item.versions
      .filter(v => !CALENDAR_PLATFORMS.some(p => p.key === v.platform))
      .map(v => platformMeta(v.platform));
    const tabs = [...known, ...extras];
    return tabs.length > 0 ? tabs : CALENDAR_PLATFORMS;
  }, [item.versions]);

  // 这条内容在当前渠道该带的变体码。没挂 campaign 就没有码,不占位。
  const activeVariantCode = useMemo(
    () => variantCode(item.campaign?.code, activePlatform),
    [item.campaign?.code, activePlatform]
  );

  const dateOptions = useMemo(() => {
    // [修 2026-08-28] 未排期(池内)条目 date=null,原来直接返回 [] —— "Pick a day"
    // 展开是空列表,弹窗排期这条路是死的。回落到当前月份的日期列表,让池内条目
    // 也能从弹窗排期(要排下月的仍可拖拽/翻月后拖)。
    // 空串也要回落:date 的初值是 `item.date ?? ''`(不是 null),`??` 对空串
    // 不生效 —— 2026-08-28 修完仍是 0 项就是这个原因,daysInMonth('') 得 NaN。
    // [2026-08-29] 回落到「今天」而不是 toISOString() —— 后者是 UTC 日期,曾经是链路上的
    // 第三种时区:纽约店晚上 8 点后打开,月份列表已经跳到"明天"所在的月。
    // [2026-09-10] 那个「今天」就是**浏览器**的。全链路一块表之后,这里没有第二个候选了。
    const base = date || todayIsoInZone(null);
    const total = daysInMonth(base);
    const [y, m] = base.split('-');
    return Array.from({ length: total }, (_, i) => {
      const d = String(i + 1).padStart(2, '0');
      const iso = `${y}-${m}-${d}`;
      return { value: iso, label: dayLabel(iso) };
    });
  }, [date]);

  const switchPlatform = (p: PlatformKey) => {
    setActivePlatform(p);
    setEditing(false); // 切平台退出编辑，避免把改动带到别的平台版本
  };

  const startEdit = () => {
    if (!version) return;
    setEditTitle(version.title);
    setEditBody(version.body);
    setEditing(true);
  };

  const saveEdit = () => {
    onSaveVersion?.(activePlatform, editTitle, editBody);
    setEditing(false);
  };

  const charLimit = version?.charLimit; // 无上限的平台(博客)不显示计数
  // 博客给全量富文本工具条,社媒给轻量的那套 —— 同 DraftEditModal 的判断。
  const isBlogPlatform = activePlatform === 'shopify_blog';
  // 平台退回的是内容本身时,重发是死循环 —— 提示改口,主动作换成 Regenerate。
  const rejectedForContent =
    version?.failStage === 'publish' && rejectedForItsContent(version?.failReason);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
        {/* 顶部只有一个关闭 X（Figma：标题在正文区，不在 header 条） */}
        <div className="flex justify-end px-5 pt-5">
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6">
          {/* 标题 + gen + setup/campaign 标签 */}
          <h2 className="text-2xl font-extrabold leading-snug text-gray-900">{item.title}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {item.versions.length} platform versions · gen #{item.genCount}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#F0EBFF] px-3 py-1 text-xs font-semibold text-purple-700">
              {setupLabel(item)}
            </span>
            {item.campaign && (
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                {item.campaign.label}
              </span>
            )}
          </div>

          {/* 改期：日期 + 时间下拉。已发布的只陈述发出去的时刻 —— 原来这两个下拉照渲染，
              但 onReschedule 是 undefined，商家选完什么都没发生。 */}
          {readOnly ? (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              Published {item.date ? dayLabel(item.date) : ''}
              {item.time ? ` · ${item.time}` : ''}
            </div>
          ) : (
          <div className="mt-5 flex flex-wrap gap-3">
            <PillSelect
              value={date}
              label={date ? dayLabel(date) : 'Pick a day'}
              options={dateOptions}
              onChange={v => {
                setDate(v);
                onReschedule?.(v, time);
              }}
            />
            <PillSelect
              value={time}
              /* 池内条目(未排期)没有时间,原来这里 label 是空串 —— 下拉框只剩一个箭头。 */
              label={time || 'Pick a time'}
              options={TIME_OPTIONS.map(t => ({ value: t, label: t }))}
              onChange={v => {
                setTime(v);
                if (date) onReschedule?.(date, v);
              }}
            />
          </div>
          )}

          {/* [PRD #17 ③] 当前渠道的变体码。挂了 campaign 时,每个渠道的文案里带的是
              主码派生的变体码(WINTER15 → WINTER15-IG),核销即算该渠道的销售 ——
              这里显示的就是商家该放进这条文案里的那个码。切 tab 会跟着变。
              前端派生而非等后端下发:规则是确定性的,且池内条目可能还没发布过,
              后端没有任何东西可返回。 */}
          {activeVariantCode && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-blue-50/60 px-3 py-2 ring-1 ring-blue-100">
              {/* [3.5 附-1] 商家看到的这个东西统一叫 channel code —— 卡片侧
                  (CampaignDetailsPanel / CampaignCard「Channel codes」)一直是这么叫的,
                  这里原来单独把 Instagram 那一份写成「Instagram code」,是漏网的一处,拉齐。 */}
              <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                Channel code
              </span>
              <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-800 ring-1 ring-blue-200">
                {activeVariantCode}
              </span>
              <span className="text-[11px] text-blue-700/80">
                {activePlatform === 'instagram'
                  ? 'put this in the caption — a redemption is how this post gets credited, since IG captions carry no link'
                  : 'redemptions of this code are credited to this channel'}
              </span>
            </div>
          )}

          {/* 平台 tab */}
          <div className="mt-6 flex items-center gap-7 border-b border-gray-200">
            {platformTabs.map(p => {
              const active = p.key === activePlatform;
              return (
                <button
                  key={p.key}
                  onClick={() => switchPlatform(p.key)}
                  className={
                    'flex items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition ' +
                    (active
                      ? 'border-purple-600 text-purple-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700')
                  }
                >
                  <img src={p.icon} alt="" className="h-5 w-5 object-contain" />
                  {p.label}
                </button>
              );
            })}
          </div>

          {editing ? (
            /* ---------- 编辑态 ---------- */
            <div className="mt-5">
              <p className="text-right text-sm font-medium text-gray-500">Editing…</p>
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-base font-bold text-gray-900 outline-none focus:border-purple-400"
                placeholder="Title"
              />
              {/* [需求⑯·修复] Hero 图在编辑态也保留展示——图片不可编辑，仅改标题/正文；
                  原先 <img> 只在只读态渲染，点 Edit 后整块被替换导致图消失（preview 有、edit 没有）。 */}
              {version?.imageUrl && (
                <img
                  src={version.imageUrl}
                  alt=""
                  className={`mt-4 max-h-96 w-full rounded-2xl ${HERO_IMG_FIT}`}
                />
              )}
              {/* [需求⑦·平台原生编辑器 · 2026-08-30] 这里原来是一个纯 textarea:博客改稿
                  没有任何富文本工具条,商家要么写纯文本、要么手打 <p>/<strong>。需求⑦
                  的编辑器 RichDraftEditor 早就有了(Blog 全量工具条、社媒 lightweight +
                  字数上限),只是当时只接在旧的 Drafts tab 的 DraftEditModal 上,而商家
                  现在走的是 Calendar 这条路。两处用同一个组件,不再各写一套。
                  字数计数由编辑器自己出(charLimit 有值时显示 x/limit)。 */}
              <div className="mt-4">
                <RichDraftEditor
                  value={editBody}
                  onChange={setEditBody}
                  imagePlacement={isBlogPlatform ? 'inline' : 'top'}
                  lightweight={!isBlogPlatform}
                  charLimit={charLimit}
                />
              </div>
            </div>
          ) : (
            /* ---------- 只读态 ---------- */
            <>
              {/* 失败版本：琥珀告警 + Retry */}
              {version?.status === 'failed' && (
                <div className="mt-5 flex gap-4 rounded-2xl bg-amber-50 p-5">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="min-w-0">
                    {/* 说清是哪一步失败的(PRD #16 ②③):Retry 的动作不同,商家该做的判断也不同 ——
                        「没写出来」要重跑 writer,「没发出去」内容是好的、只需再发一次。 */}
                    <p className="text-base font-bold text-gray-900">
                      {version.failStage === 'publish' ? 'Publish failed' : 'Generation failed'}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600">
                      {version.failReason}
                    </p>
                    {/* 发布失败的默认动作就是原样再发一次 —— 大多数是一时的(令牌、限流、
                        平台抖动),内容没毛病。唯一的例外是平台嫌**内容本身**不合规:
                        caption 超字数这种,重发一百次还是同一份内容。那时候要说清"重发
                        没用",并把 Regenerate 提到主位。 */}
                    {rejectedForContent && (
                      <p className="mt-2 rounded-xl bg-amber-100/70 px-3 py-2 text-sm font-medium text-amber-900">
                        Sending it again will not help — the platform turned down the copy itself,
                        not the delivery. Regenerate it shorter and it will go out.
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-4">
                      {version.failStage === 'publish' && onPublishAsIs && !rejectedForContent && (
                        <button
                          onClick={() => onPublishAsIs(activePlatform)}
                          className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                          Publish again
                        </button>
                      )}
                      <button
                        onClick={() => void runAction('retry', () => onRetry?.(activePlatform, note))}
                        disabled={busyAction !== null}
                        className={
                          (version.failStage === 'publish' && !rejectedForContent
                            ? 'text-sm font-semibold text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline'
                            : 'inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700') +
                          ' disabled:cursor-not-allowed disabled:opacity-60'
                        }
                      >
                        {version.failStage === 'publish' && !rejectedForContent ? null : (
                          <ArrowPathIcon
                            className={
                              'h-4 w-4' + (busyAction === 'retry' ? ' animate-spin' : '')
                            }
                          />
                        )}
                        {busyAction === 'retry'
                          ? 'Opening the chat…'
                          : version.failStage === 'publish'
                            ? 'Regenerate with Maya'
                            : 'Retry'}
                      </button>
                      {version.failStage === 'publish' && onPublishAsIs && rejectedForContent && (
                        <button
                          onClick={() => onPublishAsIs(activePlatform)}
                          className="text-sm font-semibold text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
                        >
                          Publish again anyway
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {version.failStage === 'publish'
                        ? 'Regenerate reopens the chat this piece came from, with what went wrong and anything you type above.'
                        : 'Retry reopens the chat this piece came from so Maya rewrites it with what went wrong.'}
                    </p>
                  </div>
                </div>
              )}

              {/* 正文预览。[UI 更新 2026-08-26] 稿上 Edit 按钮不在标题行,挪到正文框下方右对齐
                  (见下方) —— 编辑的是「当前平台这一版」,紧跟着它编辑的那段内容更好读。 */}
              <h3 className="mt-6 text-base font-bold text-gray-900">{version?.title}</h3>
              {version?.imageUrl && (
                <img
                  src={version.imageUrl}
                  alt=""
                  className={`mt-3 max-h-96 w-full rounded-2xl ${HERO_IMG_FIT}`}
                />
              )}
              <div className="mt-3 max-h-80 overflow-y-auto whitespace-pre-line rounded-2xl bg-gray-50 p-5 text-[15px] leading-relaxed text-gray-700">
                {/* 存储里的 blog 正文是 body_html —— 读视图转纯文本保留段落(同 Published 的修法),
                    否则商家看到 <p>/<strong> 源码。编辑态(textarea)保持原文,平台原生编辑器是另一条 PRD。 */}
                <DraftBodyText segments={bodySegments(version?.body, { preserveBreaks: true })} />
              </div>

              {/* [UI 更新 2026-08-26] Edit this version —— 正文框下方、右对齐。
                  已发布的这一格换成「View post」(设计稿里的那个入口):改这边的副本
                  改不动平台上的帖子,能做的只有去看它。

                  [3.5 #3] 原来这里是 `version?.externalUrl && <a>` —— 没拿到地址就整个不渲染。
                  验收 #20 说的「Facebook 已发布预览时有时无 View post」就是它:商家分不清
                  是没存链接还是前端没画。PRD 明确改口径 —— **不允许直接不显示**,拿不到
                  链接就置灰并注明原因。 */}
              <div className="mt-3 flex flex-col items-end gap-1">
                {readOnly ? (
                  version?.externalUrl ? (
                    <a
                      href={version.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl border border-purple-300 px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      View post
                    </a>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled
                        title="No link was recorded for this post"
                        className="inline-flex cursor-not-allowed items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-400"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        View post
                      </button>
                      <span className="text-xs text-gray-400">
                        No link was recorded for this post
                      </span>
                    </>
                  )
                ) : (
                  <button
                    onClick={startEdit}
                    className="inline-flex items-center gap-2 rounded-xl border border-purple-300 px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
                  >
                    <img src={PencilSvg} alt="" className="h-5 w-5" />
                    Edit this version
                  </button>
                )}
              </div>

              {/* [PRD 2026-08-18] 改稿统一到 chat：整篇 Regenerate，点了跳 Maya Chat，不在弹窗内闭环。
                  [对齐 UI] 标题单独一行；输入框与按钮同一行（原先标题+按钮一行、输入框换行占满宽度）。

                  [3.5 #3] 已发布的不给这一块。改期下拉、Edit this version、Discard Task 早就
                  按 readOnly 藏了,只有这块漏了 —— 已经发出去的帖子还摆一个「Regenerate in
                  chat」,点下去 Maya 会真的重写一篇,而平台上那条纹丝不动。 */}
              {!readOnly && (
                <>
              <h3 className="mt-6 text-base font-bold text-gray-900">Regenerate in chat</h3>
              <div className="mt-3 flex items-center gap-3">
                <input
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Tell Maya what to change (optional) — e.g. livelier tone"
                  className="min-w-0 flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-purple-400"
                />
                <button
                  onClick={() =>
                    void runAction('regen', () => onRegenerateInChat?.(note, activePlatform))
                  }
                  disabled={busyAction !== null}
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl border border-purple-300 px-4 py-3 text-sm font-semibold text-purple-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <img src={RefreshCwSvg} alt=""
                    className={'h-5 w-5' + (busyAction === 'regen' ? ' animate-spin' : '')}
                  />
                  {busyAction === 'regen' ? 'Opening the chat…' : 'Regenerate in chat'}
                </button>
              </div>
              {/* [UI 更新 2026-08-26] 稿上的说明文案：留空她会先问哪里不对。 */}
              <p className="mt-3 text-sm text-gray-500">
                Opens Maya Chat and sends this as a message — leave the box empty and she'll ask
                what's off first.
              </p>
                </>
              )}
            </>
          )}
        </div>

        {/* 底部操作条：编辑态 Cancel/Save；只读态 Discard | Publish Now + Done */}
        <div className="flex items-center gap-3 border-t border-gray-200 px-8 py-5">
          {editing ? (
            <>
              <button
                onClick={() => setEditing(false)}
                className="ml-auto rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                className="rounded-xl bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-purple-700"
              >
                Save
              </button>
            </>
          ) : (
            <>
              {/* 已发布不可撤回:发布记录是既成事实,而它背后的存储行 Discard 是真删。 */}
              {!readOnly && (
                <button
                  onClick={onDiscard}
                  className="rounded-xl border border-red-300 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  Discard Task
                </button>
              )}
              {onPublishNow && (
                <button
                  onClick={onPublishNow}
                  className="ml-auto rounded-xl border border-purple-300 px-5 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50"
                >
                  Publish Now
                </button>
              )}
              <button
                onClick={onClose}
                className={
                  // 只读态没有 Discard / Publish Now，Done 自己要靠右。
                  (readOnly ? 'ml-auto ' : '') +
                  'rounded-xl bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-purple-700'
                }
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
